import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { Rss } from 'lucide-react';
import { Shell } from '../components/layout/Shell';
import { ActivityFeedCard } from '../components/community/ActivityFeedCard';
import { useActivityFeed } from '../hooks/useActivityFeed';
import { useTranslation } from '../i18n';
import { Route as rootRoute } from './__root';
import styles from './activity.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activity',
  component: ActivityPage,
});

const EVENT_TYPES = ['all', 'item_listed', 'trade_completed', 'member_joined', 'item_showcased'] as const;

function ActivityPage() {
  const { t } = useTranslation();
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(1);

  const query: Record<string, string> = { page: String(page), limit: '20' };
  if (typeFilter !== 'all') query.type = typeFilter;

  const { data, isLoading } = useActivityFeed(query);

  return (
    <Shell>
      <div className={styles.page}>
        <h1 className={styles.title}>
          <Rss size={20} strokeWidth={1.75} />
          {t('activity.title')}
        </h1>

        {/* Filter tabs */}
        <div className={styles.tabs}>
          {EVENT_TYPES.map((type) => (
            <button
              key={type}
              className={`${styles.tab} ${typeFilter === type ? styles.tabActive : ''}`}
              onClick={() => { setTypeFilter(type); setPage(1); }}
            >
              {t(`activity.filter.${type}` as any)}
            </button>
          ))}
        </div>

        {/* Feed */}
        {isLoading ? (
          <div className={styles.loading}>{t('common.loading')}</div>
        ) : !data || data.data.length === 0 ? (
          <div className={styles.empty}>
            <Rss size={48} strokeWidth={1} />
            <div className={styles.emptyTitle}>{t('activity.emptyTitle')}</div>
            <div className={styles.emptyDesc}>{t('activity.emptyDesc')}</div>
          </div>
        ) : (
          <>
            <div className={styles.feedList}>
              {data.data.map((event) => (
                <ActivityFeedCard key={event.id} event={event} />
              ))}
            </div>

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageBtn}
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  {t('common.previous')}
                </button>
                <span className={styles.pageInfo}>
                  {page} / {data.totalPages}
                </span>
                <button
                  className={styles.pageBtn}
                  disabled={page >= data.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  {t('common.next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}
